import { ButtonGroup, PrimaryButton } from "@digipost/dds-buttons-react";
import { Heading } from "@digipost/dds-heading-react";
import Infobox from "@digipost/dds-infobox-react";
import { Input } from "@digipost/dds-input-react";
import { Spinner } from "@digipost/dds-spinner-react";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChangeEvent, FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Dispatch } from "../infrastructure/redux/actions";
import {
  completeRegistrationAction,
  RegistrationStatus,
  setUserInputAction,
} from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import Translated from "../infrastructure/Translated";
import { useTranslate } from "../infrastructure/useTranslate";
import styles from "./OrganisationAccountDetails.module.css";
import { Postmottak } from "./Postmottak";
import RegisterFlowStepper from "./RegisterFlowStepper";

export const OrganisationAccountDetails: FC = () => {
  const translate = useTranslate();
  const dispatch = useDispatch<Dispatch>();
  const userInput = useSelector((state: ReduxState) => state.org.userInput);
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );

  const submitting = registrationStatus === RegistrationStatus.SUBMITTING;

  const validationRules = useSelector(
    (state: ReduxState) => state.app.validationRules,
  );

  const [showPhoneValidationError, setShowPhoneValidationError] =
    useState<boolean>(false);
  const [showEmailValidationError, setShowEmailValidationError] =
    useState<boolean>(false);

  const isValidFormValue = (
    value: string,
    required: boolean,
    type: "email" | "phoneNumber",
  ): boolean => {
    const validationRule = validationRules[type];
    const emptyAndRequired = !value && required;
    return (
      !emptyAndRequired &&
      (!validationRule || !value || RegExp(validationRule).test(value))
    );
  };

  const { integrationType, mode } = useSelector(
    (state: ReduxState) => state.app.params,
  );
  const erBehandlerFlyt = mode === "behandler";

  const handleRegistrationButton = (): void => {
    setShowEmailValidationError(false);
    setShowPhoneValidationError(false);

    const validEmail = isValidFormValue(userInput.email, true, "email");
    const validPhone = isValidFormValue(userInput.phone, true, "phoneNumber");

    if (validEmail && validPhone) {
      dispatch(
        completeRegistrationAction(false, erBehandlerFlyt, integrationType),
      );
    }
    if (!validEmail) {
      setShowEmailValidationError(!validEmail);
    }
    if (!validPhone) {
      setShowPhoneValidationError(!validPhone);
    }
  };

  return (
    <div>
      <Heading level={1} size="large">
        <Translated textKey="register.org.registration_form.title" />
      </Heading>

      <RegisterFlowStepper currentStep="ACCOUNT_DETAILS" />

      <Heading level={2} size="medium">
        <Translated textKey="register.org.registration_form.contact_information" />
      </Heading>

      <Input
        labelText={translate("register.your_email")}
        errorText={
          showEmailValidationError
            ? translate("register.org.registration_form.invalid_email")
            : undefined
        }
        errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          dispatch(setUserInputAction({ email: event.target.value }));
        }}
        valid={!showEmailValidationError}
        value={userInput.email}
        name="email"
      />
      <Input
        labelText={translate("register.your_phone_number")}
        errorText={
          showPhoneValidationError
            ? translate("register.org.registration_form.invalid_phone")
            : undefined
        }
        errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
        onChange={(event) => {
          dispatch(setUserInputAction({ phone: event.target.value }));
        }}
        valid={!showPhoneValidationError}
        value={userInput.phone}
        name="phone"
      />

      <Postmottak
        value={userInput.mailbox}
        onChange={(val) =>
          dispatch(setUserInputAction({ mailbox: val.target.value === "true" }))
        }
      />

      <div role="alert">
        {registrationStatus === RegistrationStatus.FAILED && (
          <Infobox
            className={styles.errorBox}
            title={translate("register.org.error.general_header")}
            variant="error"
            level={2}
          />
        )}
      </div>

      <ButtonGroup>
        <PrimaryButton
          onClick={handleRegistrationButton}
          data-testid="bedrift-account-details-register-button"
          disabled={submitting}
          iconRight={submitting && <Spinner />}
        >
          <Translated textKey="register.org.register_button" />
        </PrimaryButton>
      </ButtonGroup>
    </div>
  );
};
